import { IConfigData, ConfigurationService } from 'services/ConfigurationService';
import { action, configure, makeAutoObservable, runInAction } from 'mobx';

import { AppSwitcherIcon } from '@trackman/web-shared-components/features/TopNavigation/IconMenus/AppSwitcher/icons/MenuIcon';
import { ApplicationUserRights } from './ApplicationUserRightsStore';
import { AuthenticationService } from 'services/AuthenticationService';
import { NotificationsStore } from './NotificationsStore';
import StartupService from 'services/StartupService';
import { jwtDecode } from 'jwt-decode';

configure({ enforceActions: 'never' });

type BearerTokenProfileInfo = {
  permissions: string[];
  name: string;
  surname: string;
  userId: string;
  identityUrl: string;
  preferredLanguage: string;
  preferredUnitSystem: string;
  sport: string;
  organizationId: string;
  playerId: string;
  umpireId: string;
  organizationName: string;
};

// Bearer token structure depends on a backend configuration, so all keys are optional
type BearerToken = {
  iss?: string;
  nbf?: number;
  iat?: number;
  exp?: number;
  aud?: string[];
  scope?: string[];
  amr?: string[];
  client_id?: string;
  sub?: string;
  auth_time?: number;
  idp?: string;
  permission?: string[] | string;
  'preferred-language'?: string;
  'preferred-unit-system'?: string;
  'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/givenname'?: string;
  'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/surname'?: string;
  sport?: string;
  organizationid?: string;
  playerid?: string;
  umpireid?: string;
  organizationname?: string;
  sid?: string;
  jti?: string;
};

export type App = {
  id: string;
  display_name: string;
  description: string;
  group: string;
  icon_name: AppSwitcherIcon;
  client_id: string;
  required_permission: string;
  base_url: string;
  display_name_lng_key: string;
  description_lng_key: string;
  group_lng_key: string;
  updated: string;
};

export type Profile = {
  userId: string;
  firstName: string;
  lastName: string;
  email: string;
  profilePhotoUrl: string;
  preferredLanguage: string;
  preferredUnitSystem: string;
  myAccountUrl: string;
  authority: string;
  twoFactorEnabled: boolean;
};

export class StartupStore {
  private authenticationService: AuthenticationService;
  private configurationService: ConfigurationService;
  private startupService: StartupService;
  private ns: NotificationsStore;

  constructor(
    authenticationService: AuthenticationService,
    configurationService: ConfigurationService,
    startupService: StartupService,
    ns: NotificationsStore,
  ) {
    makeAutoObservable(this);
    this.authenticationService = authenticationService;
    this.configurationService = configurationService;
    this.startupService = startupService;
    this.ns = ns;
  }

  isInitializing: boolean = false;
  hasBearerToken: boolean = false;
  isLoadingConfig: boolean = false;
  isLoadingApps: boolean = false;
  isLoadingProfile: boolean = false;
  isLoadingPermissions: boolean = false;

  bearerTokenProfileInfo: BearerTokenProfileInfo | null = null;
  config: IConfigData | null = null;
  apps: App[] = [];
  profile: Profile | null = null;
  permissions: string[] = [];

  initialize = async () => {
    runInAction(() => { this.isInitializing = true; });

    await this.getToken();

    // Once the token is obtained all remaining data can be fetched simultaneously
    await Promise.all([
      this.getConfig(), // needed by Profile menu
      this.getApps(), // needed by AppSwitcher
      //this.getPermissions(), // Permissions are part of the token, so unless your app specifically needs this endpoint use token data instead for better loading performance
      this.getProfile(), // Profile info are part of the token, and this call is only needed for profile pic URL. Once it is part of the token it can also be omitted.
    ]);

    runInAction(() => { this.isInitializing = false; });
  };

  getToken = async () => {
    await this.authenticationService.initialize();
    const authData = this.authenticationService.getAuthData();
    if (authData) {
      const decodedToken = jwtDecode<BearerToken>(authData.accessToken);
      const permissionsArray = decodedToken.permission
        ? Array.isArray(decodedToken.permission)
          ? decodedToken.permission
          : [decodedToken.permission]
        : [];
      runInAction(() => {
        this.bearerTokenProfileInfo = {
          permissions: permissionsArray,
          name: decodedToken['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/givenname'] ?? '',
          surname: decodedToken['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/surname'] ?? '',
          userId: decodedToken.sub ?? '',
          identityUrl: decodedToken.iss ?? '',
          preferredLanguage: decodedToken['preferred-language'] ?? '',
          preferredUnitSystem: decodedToken['preferred-unit-system'] ?? '',
          sport: decodedToken.sport ?? '',
          organizationId: decodedToken.organizationid ?? '',
          playerId: decodedToken.playerid ?? '',
          umpireId: decodedToken.umpireid ?? '',
          organizationName: decodedToken.organizationname ?? '',
        };
      });
      
      ApplicationUserRights.setData({ permissions: this.bearerTokenProfileInfo?.permissions ?? [] });
      runInAction(() => { this.hasBearerToken = true; });
    }
  };

  getConfig = async () => {
    try {
      runInAction(() => { this.isLoadingConfig = true; });
      const response = await this.configurationService.getConfigs();
      runInAction(() => { this.config = response; });
    } catch (ex: any) {
      this.ns.addToast('Error', ex.message, 'error');
    }
    runInAction(() => { this.isLoadingConfig = false; });
  };

  getApps = async () => {
    if (!this.bearerTokenProfileInfo) return;
    try {
      runInAction(() => { this.isLoadingApps = true; });
      const response = await this.startupService.getAllApps(this.bearerTokenProfileInfo.identityUrl);
      runInAction(() => { this.apps = response; });
    } catch (ex: any) {
      this.ns.addToast('Error', ex.message, 'error');
    }
    runInAction(() => { this.isLoadingApps = false; });
  };

  getProfile = async () => {
    if (!this.bearerTokenProfileInfo) return;
    try {
      runInAction(() => { this.isLoadingProfile = true; });
      const response = await this.startupService.getProfile();
      runInAction(() => { this.profile = response; });
    } catch (ex: any) {
      this.ns.addToast('Error', ex.message, 'error');
    }
    runInAction(() => { this.isLoadingProfile = false; });
  };

  getPermissions = async () => {
    if (!this.bearerTokenProfileInfo) return;
    try {
      runInAction(() => { this.isLoadingPermissions = true; });
      const response = await this.startupService.getPermissions();
      runInAction(() => { this.permissions = response.permissions; });
    } catch (ex: any) {
      this.ns.addToast('Error', ex.message, 'error');
    }
    runInAction(() => { this.isLoadingPermissions = false; });
  };
}

export default StartupStore;
